import React from 'react';
import { Wallet, Clock, Users, Award } from 'lucide-react';
import { Money } from '@/components/common/Money';

interface DashboardKpiCardsProps {
  availableBalanceMinor: number;
  pendingClearanceMinor: number;
  activeSubscriptions: number;
  lifetimeEarningsMinor: number;
  commissionRatePercent: number;
}

export const DashboardKpiCards: React.FC<DashboardKpiCardsProps> = ({
  availableBalanceMinor,
  pendingClearanceMinor,
  activeSubscriptions,
  lifetimeEarningsMinor,
  commissionRatePercent,
}) => {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      {/* 1. Available Balance */}
      <div className="bg-zinc-900/70 border border-zinc-800/80 rounded-2xl p-5 hover:border-zinc-700 transition relative overflow-hidden group">
        <div className="flex items-center justify-between text-zinc-400 mb-3">
          <span className="text-xs font-medium uppercase tracking-wider">Available for Payout</span>
          <div className="w-8 h-8 rounded-lg bg-indigo-500/10 border border-indigo-500/20 flex items-center justify-center text-indigo-400">
            <Wallet className="w-4 h-4" />
          </div>
        </div>
        <div className="text-2xl font-bold text-white tracking-tight">
          <Money amountMinor={availableBalanceMinor} />
        </div>
        <p className="text-xs text-zinc-400 mt-2">
          {availableBalanceMinor >= 100000 ? (
            <span className="text-emerald-400 font-medium">Eligible for withdrawal</span>
          ) : (
            <span>Minimum payout threshold: ৳1,000</span>
          )}
        </p>
      </div>

      {/* 2. Pending Clearance */}
      <div className="bg-zinc-900/70 border border-zinc-800/80 rounded-2xl p-5 hover:border-zinc-700 transition relative overflow-hidden group">
        <div className="flex items-center justify-between text-zinc-400 mb-3">
          <span className="text-xs font-medium uppercase tracking-wider">Pending Clearance</span>
          <div className="w-8 h-8 rounded-lg bg-amber-500/10 border border-amber-500/20 flex items-center justify-center text-amber-400">
            <Clock className="w-4 h-4" />
          </div>
        </div>
        <div className="text-2xl font-bold text-white tracking-tight">
          <Money amountMinor={pendingClearanceMinor} />
        </div>
        <p className="text-xs text-zinc-400 mt-2">
          Clears after 14-day customer guarantee
        </p>
      </div>

      {/* 3. Active Referred Merchants */}
      <div className="bg-zinc-900/70 border border-zinc-800/80 rounded-2xl p-5 hover:border-zinc-700 transition relative overflow-hidden group">
        <div className="flex items-center justify-between text-zinc-400 mb-3">
          <span className="text-xs font-medium uppercase tracking-wider">Active Customers</span>
          <div className="w-8 h-8 rounded-lg bg-blue-500/10 border border-blue-500/20 flex items-center justify-center text-blue-400">
            <Users className="w-4 h-4" />
          </div>
        </div>
        <div className="text-2xl font-bold text-white tracking-tight">
          {activeSubscriptions}{' '}
          <span className="text-sm font-normal text-zinc-400">
            {activeSubscriptions === 1 ? 'store' : 'stores'}
          </span>
        </div>
        <p className="text-xs text-zinc-400 mt-2">
          Earning {commissionRatePercent}% recurring revenue share
        </p>
      </div>

      {/* 4. Lifetime Earnings */}
      <div className="bg-zinc-900/70 border border-zinc-800/80 rounded-2xl p-5 hover:border-zinc-700 transition relative overflow-hidden group">
        <div className="flex items-center justify-between text-zinc-400 mb-3">
          <span className="text-xs font-medium uppercase tracking-wider">Lifetime Earnings</span>
          <div className="w-8 h-8 rounded-lg bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center text-emerald-400">
            <Award className="w-4 h-4" />
          </div>
        </div>
        <div className="text-2xl font-bold text-white tracking-tight">
          <Money amountMinor={lifetimeEarningsMinor} />
        </div>
        <p className="text-xs text-zinc-400 mt-2">
          Disbursed via bKash, Nagad, or Bank
        </p>
      </div>
    </div>
  );
};
